import React, {useEffect, useState, useContext} from 'react';
import {Block} from '../block';
import {Input, Typography} from 'antd';
import {set, cloneDeep, isUndefined} from 'lodash';
import {OpenApiContext} from '../../openapi-context';
import {MarkdownBlock, MarkdownView} from '../markdown-block';

// info.license has three displayed fields: name + url (OpenAPI standard) and
// description (editor-side extension stored on the same object so the spec
// stays a single round-trippable YAML document — most tools tolerate
// unknown fields on info.license; if a strict validator rejects it we can
// re-key as `x-description` later without UI churn).
//
// The description field uses MarkdownBlock — same MDX-backed editor as
// info.description on the overview page — so users can format the license
// notes as rich text (links, bold, lists) instead of plain string.
export function LicenseBlock({schema, className, onCancel, onSave, rootPath, checkView}) {
    const {state, dispatch} = useContext(OpenApiContext);
    const [editedSchema, setEditedSchema] = useState();
    const [licenseData, setLicenseData] = useState();

    useEffect(() => {
        if (!isUndefined(schema)) {
            setEditedSchema(cloneDeep(schema));
            const src = schema.license || {};
            const license = {
                name: src.name || '',
                url: src.url || '',
                description: src.description || '',
            };
            setLicenseData(license);
            if (checkView) {
                checkView();
            }
        }
    }, [schema]);

    function handleSave() {
        dispatch({type: 'terminate'});
        return onSave && onSave(editedSchema);
    }

    function handleCancel() {
        dispatch({type: 'terminate'});
        return onCancel && onCancel();
    }

    const triggerEdit = () => dispatch({type: 'activate', message: 'changes to specification license'});

    const writeField = (field) => (value) => {
        licenseData[field] = value;
        set(editedSchema, 'license', {...licenseData});
    };

    const setLicenseName = (e) => writeField('name')(e.target.value);
    const setLicenseUrl = (e) => writeField('url')(e.target.value);
    const setLicenseDescription = (value) => writeField('description')(value);

    if (isUndefined(editedSchema) || isUndefined(licenseData)) return null;

    const hasAny = !!(licenseData.name || licenseData.url || licenseData.description);

    const LicenseEdit = () => (
        <div className="frontpage license-block">
            <div className="serverView">
                <Typography.Title level={2}>License</Typography.Title>
            </div>
            <div className="license-field">
                <Typography.Text strong className="license-field-label">Name</Typography.Text>
                <Input
                    defaultValue={licenseData.name}
                    placeholder="License (MIT, Apache 2.0, etc)"
                    onChange={setLicenseName}
                />
            </div>
            <div className="license-field">
                <Typography.Text strong className="license-field-label">URL</Typography.Text>
                <Input
                    defaultValue={licenseData.url}
                    onChange={setLicenseUrl}
                    placeholder="https://example.com/license"
                />
            </div>
            <div className="license-field">
                <Typography.Text strong className="license-field-label">Description</Typography.Text>
                <MarkdownBlock
                    source={licenseData.description}
                    size={400}
                    placeholder="Optional notes about the license (markdown supported)"
                    onValue={setLicenseDescription}
                    delegatedEditMode={true}
                    imageRootPath={rootPath}
                />
            </div>
        </div>
    );

    const LicenseView = () => (
        <div className="frontpage license-block">
            <div className="serverView">
                <Typography.Title level={2}>License</Typography.Title>
            </div>
            {hasAny ? (
                <>
                    <div className="license-field">
                        <Typography.Text strong className="license-field-label">Name</Typography.Text>
                        <Typography.Paragraph className="license-field-value">
                            {licenseData.name || <Typography.Text type="secondary">(not set)</Typography.Text>}
                        </Typography.Paragraph>
                    </div>
                    <div className="license-field">
                        <Typography.Text strong className="license-field-label">URL</Typography.Text>
                        <Typography.Paragraph className="license-field-value">
                            {licenseData.url
                                ? <a href={licenseData.url} target="_blank" rel="noreferrer">{licenseData.url}</a>
                                : <Typography.Text type="secondary">(not set)</Typography.Text>}
                        </Typography.Paragraph>
                    </div>
                    <div className="license-field">
                        <Typography.Text strong className="license-field-label">Description</Typography.Text>
                        {licenseData.description ? (
                            <MarkdownView source={licenseData.description} />
                        ) : (
                            <Typography.Paragraph className="license-field-value">
                                <Typography.Text type="secondary">(not set)</Typography.Text>
                            </Typography.Paragraph>
                        )}
                    </div>
                </>
            ) : (
                <Typography.Paragraph type="secondary" className="license-empty">
                    No license is set on this specification.
                    Click the edit pencil to add one (e.g. MIT, Apache-2.0).
                </Typography.Paragraph>
            )}
        </div>
    );

    return (
        <Block className={className} onCancel={handleCancel} onSave={handleSave} onEdit={triggerEdit}>
            {state.editMode ? <LicenseEdit /> : <LicenseView />}
        </Block>
    );
}
